import type { Ref } from 'vue'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import type { PublicStore } from '~/types/PublicStore'
import type { StoreOrderingContextInputs } from './StoreOrderingContextInputs'
import type { TranslateFn } from './TranslateFn'
import type { LocalizedMessage } from '~/interfaces/LocalizedMessage'

// Pure: the optional eventDate/eventTime the store-fetch call expects, given the caller's raw
// selection (an empty string/nullish time means "no time selected yet").
function resolveStoreFetchDateParams(
  eventDate: string,
  eventTime: string | null | undefined
): { eventDate: string | undefined; eventTime: string | undefined } {
  return {
    eventDate: eventDate !== '' ? eventDate : undefined,
    eventTime: eventTime != null && eventTime !== '' ? eventTime : undefined
  }
}

function resolveStoreFetchErrorMessage(err: unknown, t: TranslateFn): string {
  return resolveProblemMessage(
    AppProblemModel.normalize(err, { context: 'store page' }),
    t,
    t('dashboard.messages.failedToLoadStoreDetails')
  )
}

// Split out of `performLoadStoreContext` so that function stays a flat sequence of
// loading-state/staleness guards instead of nesting this fetch/commit sequence deeper.
async function fetchAndCommitStoreResult(
  requestKey: string | null,
  deps: Pick<
    LoadStoreContextDeps,
    'slug' | 'eventDate' | 'eventTime' | 'userId' | 'fetchStore' | 'store' | 'storeContextKey'
  >
): Promise<void> {
  const currentSlug = deps.slug.value
  if (currentSlug === '') {
    throw new Error('Store slug is required')
  }

  const dateParams = resolveStoreFetchDateParams(deps.eventDate.value, deps.eventTime.value)
  const result = await deps.fetchStore(
    currentSlug,
    deps.userId.value,
    dateParams.eventDate,
    dateParams.eventTime
  )
  if (deps.storeContextKey.value !== requestKey) return

  deps.store.value = result
}

interface LoadStoreContextDeps {
  store: Ref<PublicStore | null>
  loading: Ref<boolean>
  refreshing: Ref<boolean>
  hasLoadedOnce: Ref<boolean>
  error: LocalizedMessage
  storeContextKey: { readonly value: string | null }
  slug: Ref<string>
  eventDate: Ref<string>
  eventTime: Ref<string | null | undefined>
  userId: Ref<number | undefined>
  fetchStore: StoreOrderingContextInputs['fetchStore']
  onStoreFetchError?: ((error: unknown) => void) | undefined
  t: TranslateFn
}

/**
 * Fetches the store resource for the current slug/date/time and commits it to `deps.store`,
 * discarding a response superseded by a newer selection (checked before and after the fetch).
 */
export async function performLoadStoreContext(deps: LoadStoreContextDeps): Promise<void> {
  const requestKey = deps.storeContextKey.value
  const isFirstLoad = !deps.hasLoadedOnce.value
  if (isFirstLoad) {
    deps.loading.value = true
  } else {
    deps.refreshing.value = true
  }
  deps.error.clear()

  try {
    await fetchAndCommitStoreResult(requestKey, deps)
  } catch (err: unknown) {
    if (deps.storeContextKey.value !== requestKey) return
    deps.onStoreFetchError?.(err)
    deps.error.set(() => resolveStoreFetchErrorMessage(err, deps.t))
  } finally {
    if (deps.storeContextKey.value === requestKey) {
      deps.hasLoadedOnce.value = true
      deps.loading.value = false
      deps.refreshing.value = false
    }
  }
}
